import React from 'react';
import { Box, Typography, Chip, ButtonBase } from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import { TOKENS } from '../theme';
import {
  actionMeta,
  entityMeta,
  entityIdentifier,
  changeRows,
  formatChangedAt,
  isManagerEntry,
  sourceMeta,
} from './auditFormat';
import useTicketPhotoViewer from './useTicketPhotoViewer';

export default function ManagerModificationsList({
  auditLog = [],
  customers = [],
  employees = [],
  manageBonusList = [],
}) {
  const lookups = { customers, employees, manageBonusList };
  const photoViewer = useTicketPhotoViewer();

  // Only show actual manager modifications — approve/flag decisions are
  // tracked separately in the bonus/raffle review surfaces, and the
  // employee's own edits/deletes and transfers live in the session Audit Log.
  const visible = auditLog.filter(
    (e) => ['CREATE', 'EDIT', 'DELETE'].includes(e?.action) && isManagerEntry(e)
  );
  if (!visible.length) return null;

  const sorted = [...visible].sort((a, b) => {
    const ta = new Date(a.changedAt || 0).getTime();
    const tb = new Date(b.changedAt || 0).getTime();
    return tb - ta;
  });

  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${TOKENS.slate200}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          background: TOKENS.slate50,
          borderBottom: `1px solid ${TOKENS.slate200}`,
          px: 1.5,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate700,
          }}
        >
          Manager Modifications
        </Typography>
        <Chip
          size="small"
          label={`${sorted.length} ${
            sorted.length === 1 ? 'change' : 'changes'
          }`}
          sx={{
            height: 18,
            fontSize: 10,
            background: TOKENS.slate100,
            color: TOKENS.slate700,
          }}
        />
      </Box>
      <Box>
        {sorted.map((entry, i) => (
          <AuditRow
            key={entry.auditId || i}
            entry={entry}
            lookups={lookups}
            isLast={i === sorted.length - 1}
            onViewPhoto={photoViewer.view}
          />
        ))}
      </Box>
      {photoViewer.node}
    </Box>
  );
}

// Row text sizes. `compact` is the Shift report panel and its PDF; `large`
// is the full-screen Audit Log and transfer history, read on its own.
const SIZES = {
  compact: {
    col: 84,
    chipH: 18,
    action: 9,
    time: 10,
    entity: 10,
    ident: 12,
    label: 10,
    labelW: 70,
    value: 11,
    by: 11,
    sourceH: 16,
    source: 9,
  },
  large: {
    col: 104,
    chipH: 24,
    action: 12,
    time: 13,
    entity: 13,
    ident: 16,
    label: 12,
    labelW: 120,
    value: 14,
    by: 14,
    sourceH: 20,
    source: 11,
  },
};

// showSource: tag the row Manager / Employee (the full Audit Log mixes both).
// showDeletedValues: list what a DELETE removed, not just which entry it was.
export function AuditRow({
  entry,
  lookups,
  isLast,
  onViewPhoto,
  showSource = false,
  showDeletedValues = false,
  large = false,
}) {
  const fs = large ? SIZES.large : SIZES.compact;
  const action = actionMeta(entry);
  const ent = entityMeta(entry.entityType);
  const ident = entityIdentifier(entry, lookups);
  const rows = changeRows(entry, lookups);
  const time = formatChangedAt(entry.changedAt);
  const who = entry.changedByName || 'Manager';
  const source = showSource ? sourceMeta(entry) : null;
  const showRows =
    rows.length > 0 &&
    (entry.action === 'EDIT' ||
      (showDeletedValues && entry.action === 'DELETE'));

  // A Ticket Out entry that carried a photo keeps its reference in the audit
  // snapshot even after edit/delete, so managers can still view it (spec §9).
  const hasPhoto =
    entry.entityType === 'TICKET_OUT' &&
    Boolean(entry.oldValues?.photoPath || entry.newValues?.photoPath);

  return (
    <Box
      // data-pdf-block — the PDF generator paginates at these boundaries so
      // an individual audit entry never splits across two pages.
      data-pdf-block="audit-row"
      sx={{
        display: 'flex',
        gap: large ? 1.5 : 1,
        px: large ? 2 : 1.5,
        py: large ? 1.75 : 1.25,
        borderBottom: isLast ? 'none' : `1px solid ${TOKENS.slate100}`,
      }}
    >
      <Box
        sx={{
          width: fs.col,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }}
      >
        <Chip
          size="small"
          label={action.label}
          sx={{
            height: fs.chipH,
            fontSize: fs.action,
            fontWeight: 700,
            background: action.bg,
            color: action.color,
            letterSpacing: '0.05em',
          }}
        />
        {time && (
          <Typography sx={{ fontSize: fs.time, color: TOKENS.slate500 }}>
            {time}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            mb: showRows ? 0.5 : 0,
          }}
        >
          <Chip
            size="small"
            label={ent.label}
            sx={{
              height: fs.chipH,
              fontSize: fs.entity,
              fontWeight: 700,
              background: ent.bg,
              color: ent.color,
            }}
          />
          {ident && (
            <Typography
              sx={{
                fontSize: fs.ident,
                fontWeight: 600,
                color: TOKENS.slate900,
              }}
            >
              {ident}
            </Typography>
          )}
          {hasPhoto && onViewPhoto && (
            <ButtonBase
              onClick={() => onViewPhoto(entry.entityId)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                background: TOKENS.violet50,
                color: TOKENS.violet700,
              }}
            >
              <PhotoCameraIcon sx={{ fontSize: 12 }} />
              <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                Photo
              </Typography>
            </ButtonBase>
          )}
        </Box>
        {showRows && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {rows.map((r) =>
              entry.action === 'DELETE' ? (
                <DeletedLine key={r.field} fs={fs} {...r} />
              ) : (
                <ChangeLine key={r.field} fs={fs} {...r} />
              )
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <Typography sx={{ fontSize: fs.by, color: TOKENS.slate500 }} noWrap>
            by{' '}
            <span style={{ fontWeight: 600, color: TOKENS.slate700 }}>
              {who}
            </span>
          </Typography>
          {source && (
            <Chip
              size="small"
              label={source.label}
              sx={{
                height: fs.sourceH,
                fontSize: fs.source,
                fontWeight: 700,
                background: source.bg,
                color: source.color,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// What a deleted entry held — one value per field, struck through.
function DeletedLine({ label, oldVal, fs }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: fs.label,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
          minWidth: fs.labelW,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: fs.value,
          color: TOKENS.red700,
          fontFamily: 'monospace',
          textDecoration: 'line-through',
        }}
      >
        {oldVal}
      </Typography>
    </Box>
  );
}

function ChangeLine({ label, oldVal, newVal, fs }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        sx={{
          fontSize: fs.label,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
          minWidth: fs.labelW,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: fs.value,
          color: TOKENS.slate500,
          fontFamily: 'monospace',
          textDecoration: 'line-through',
        }}
      >
        {oldVal}
      </Typography>
      <EastIcon sx={{ fontSize: fs.value, color: TOKENS.slate400 }} />
      <Typography
        sx={{
          fontSize: fs.value,
          fontWeight: 700,
          color: TOKENS.slate900,
          fontFamily: 'monospace',
        }}
      >
        {newVal}
      </Typography>
    </Box>
  );
}
